import { Location } from '../../models/locations-models/location';
import { ADDRESS_SUBFIELDS, ALTERNATIVE_FIELD_IDS } from './constants';
import { LSCField } from '../../models/LSCFields';
import { CCLocation } from '../../models/locations-models/cc-location';
import { OngoingActionsEnum } from './enums/ongoing-actions';
import { SocialMediaUrl } from '../../models/locations-models/social-media-urls';

export enum FieldGroup {
  DEFAULT_DISPLAY = 'Default display',
  OPENING_HOURS = 'Opening hours',
  ADDRESS = 'Location',
  MEDIA = 'Media',
  OTHER = 'Other',
  PLACE_ACTION_LINKS = 'Action & social links',
  GMB_SPECIFIC = 'GBP specific',
  BLS_SPECIFIC = 'BLS specific',
}

// TODO: alt. fieldIds are listed in ALTERNATIVE_FIELD_IDS = we could refactor the method below or
//  better yet, have BE take care of this!
/**
 * Returns a "pretty" name for a field ID (for displaying)
 * In some cases, there are alternative fieldIds (for instance location.differences can have these)
 * @param fieldID
 */
export const getFullNameForFieldID = (fieldID: string | LSCField): string => {
  switch (fieldID) {
    case LSCField.status:
      return 'Status';
    case LSCField.storeCode:
      return 'Store Code';

    case LSCField.businessName:
    case ALTERNATIVE_FIELD_IDS[LSCField.businessName]:
      return 'Business Name';

    case LSCField.city:
      return 'City';
    case LSCField.addressLine1:
      return 'Address 1';
    case LSCField.addressLine2:
      return 'Address 2';
    case LSCField.addressLine3:
      return 'Address 3';
    case LSCField.addressLine4:
      return 'Address 4';
    case LSCField.addressLine5:
      return 'Address 5';
    case 'address':
      return 'Address';
    case LSCField.state:
      return 'State';
    case LSCField.district:
      return 'District';
    case LSCField.country:
      return 'Country';
    case LSCField.postalCode:
      return 'Postal Code';

    case LSCField.website:
    case ALTERNATIVE_FIELD_IDS[LSCField.website]:
      return 'Website';

    case LSCField.gmbAccountIds:
      return 'GBP Groups';
    case LSCField.gmbLocationId:
      return 'GBP ID';
    case LSCField.businessHours:
    case 'regularHours':
      return 'Business Hours';
    case LSCField.mondayHours:
      return 'Monday Hours';
    case LSCField.tuesdayHours:
      return 'Tuesday Hours';
    case LSCField.wednesdayHours:
      return 'Wednesday Hours';
    case LSCField.thursdayHours:
      return 'Thursday Hours';
    case LSCField.fridayHours:
      return 'Friday Hours';
    case LSCField.saturdayHours:
      return 'Saturday Hours';
    case LSCField.sundayHours:
      return 'Sunday Hours';
    case LSCField.specialHours:
      return 'Special Hours';
    case LSCField.gxSpecialHours:
      return 'GX Special Hours';

    case LSCField.primaryCategory:
      return 'Primary Category';

    case LSCField.additionalCategory:
    case ALTERNATIVE_FIELD_IDS[LSCField.additionalCategory]:
      return 'Additional Categories';

    case LSCField.categories:
      return 'Primary & Additional Categories';

    case LSCField.fromTheBusiness:
    case ALTERNATIVE_FIELD_IDS[LSCField.fromTheBusiness]:
      return 'From the Business';

    case LSCField.primaryPhone:
      return 'Primary Phone';

    case LSCField.additionalPhone:
    case ALTERNATIVE_FIELD_IDS[LSCField.additionalPhone]:
      return 'Additional Phones';

    case LSCField.phoneNumbers:
      return 'Primary Phone & Additional Phones';

    case LSCField.adwordsLocation:
    case ALTERNATIVE_FIELD_IDS[LSCField.adwordsLocation]:
      return 'Google Ads Phone';

    case LSCField.openingDate:
    case 'openInfo.openingDate':
      return 'Opening Date';

    case LSCField.coverPhoto:
      return 'Cover Photo';
    case LSCField.logoPhoto:
      return 'Logo Photo';
    case LSCField.otherPhotos:
      return 'Other Photos';
    case LSCField.labels:
      return 'Labels';
    case LSCField.comment:
      return 'Comment';

    case LSCField.coordinateImport:
    case ALTERNATIVE_FIELD_IDS[LSCField.coordinateImport]:
      return 'Imported Coordinates';

    case LSCField.coordinate:
    case ALTERNATIVE_FIELD_IDS[LSCField.coordinate]: //todo rename BLS-2470
    case 'latLng':
      return 'Validated Coordinates';
    case LSCField.latitude:
      return 'Latitude';
    case LSCField.longitude:
      return 'Longitude';

    case LSCField.lastImportErrorMessage:
      return 'Last Import Error';
    case LSCField.lastUpdateErrorMessage:
      return 'Last Update Error';
    case LSCField.blackListedFields:
      return 'Blacklisted fields';

    case 'lastUpdated':
    case LSCField.editTimestamp:
      return 'Last Edited';

    case 'attributes':
      return 'Attributes';
      //preserve "Closed" for pretty field name in field edit history of old status
    case 'closed':
      return 'Closed';
    case 'temporarilyClosed':
      return 'Temporarily closed';

    case LSCField.businessStatus:
    case ALTERNATIVE_FIELD_IDS[LSCField.businessStatus]: //todo rename BLS-2470
      return 'Business Status';

    case LSCField.verified: //todo rename BLS-2470
      return 'Validation Status';
    case LSCField.managementStatus:
      return 'Management Status';
    case LSCField.existsInGMB:
      return 'Exists in GBP';
    case LSCField.differentInGMB:
      return 'Different in GBP';
    case LSCField.differences:
      return 'Open differences';
    case LSCField.acceptedDifferences:
      return 'Accepted differences';
    case 'hasServiceArea':
      return 'Has service area';
    case LSCField.myBusinessLocationUrl:
      return 'MyBusiness URL';
    case LSCField.searchUrl:
      return 'Google Search URL';
    case LSCField.mapsUrl:
      return 'Google Maps URL';

    case LSCField.appointmentURL:
    case ALTERNATIVE_FIELD_IDS[LSCField.appointmentURL]: //todo rename BLS-2470
      return 'Appointment URLs';

    case LSCField.menuURL:
    case ALTERNATIVE_FIELD_IDS[LSCField.menuURL]: //todo rename BLS-2470
      return 'Menu URL';

    case LSCField.orderAheadURL:
    case ALTERNATIVE_FIELD_IDS[LSCField.orderAheadURL]: //todo rename BLS-2470
      return 'Order Ahead URLs';

    case LSCField.reservationsURL:
    case ALTERNATIVE_FIELD_IDS[LSCField.reservationsURL]: //todo rename BLS-2470
      return 'Reservations URLs';

    case LSCField.socialMediaUrls:
      return 'Social media URLs';
    case SocialMediaUrl.FACEBOOK:
      return 'Facebook URL';
    case SocialMediaUrl.INSTAGRAM:
      return 'Instagram URL';
    case SocialMediaUrl.LINKEDIN:
      return 'LinkedIn URL';
    case SocialMediaUrl.PINTEREST:
      return 'Pinterest URL';
    case SocialMediaUrl.TIKTOK:
      return 'Tiktok URL';
    case SocialMediaUrl.TWITTER:
      return 'Twitter URL';
    case SocialMediaUrl.YOUTUBE:
      return 'Youtube URL';
    case LSCField.placeId:
      return 'Google Place ID';
    case LSCField.customServices:
      return 'Custom Services';
    case LSCField.dataGoldmine:
      return 'Data Goldmine';

    case 'serviceName':
      return 'Service name';
    case 'serviceDescription':
      return 'Description';
    case 'serviceCategoryId':
      return 'Category ID';

    case LSCField.claimAndCleanCompletionTimestamp:
      return 'C&C Completion';
    case LSCField.locationId:
    case LSCField.id:
      return 'Location ID';
    default:
      return fieldID;
  }
};

/**
 * Returns the FieldGroup a field belongs to.
 * @param fieldId Field to check for its field group
 */
export const getFieldGroupOfFieldId = (fieldId: string | keyof Location | LSCField): FieldGroup => {
  switch (fieldId) {
    case LSCField.businessStatus:
    case ALTERNATIVE_FIELD_IDS[LSCField.businessStatus]:
    case LSCField.businessName:
    case ALTERNATIVE_FIELD_IDS[LSCField.businessName]:
    case LSCField.city:
    case LSCField.country:
    case LSCField.addressLine1:
    case LSCField.website:
    case ALTERNATIVE_FIELD_IDS[LSCField.website]:
    case LSCField.businessHours:
    case ALTERNATIVE_FIELD_IDS[LSCField.businessHours]:
    case LSCField.differences:
    case LSCField.comment:
      return FieldGroup.DEFAULT_DISPLAY;
    case LSCField.addressLine2:
    case LSCField.addressLine3:
    case LSCField.addressLine4:
    case LSCField.addressLine5:
    case LSCField.address:
    case LSCField.state:
    case LSCField.district:
    case LSCField.postalCode:
    case LSCField.coordinateImport:
    case LSCField.coordinate:
      return FieldGroup.ADDRESS;
    case LSCField.specialHours:
    case LSCField.gxSpecialHours:
    case LSCField.openingDate:
    case ALTERNATIVE_FIELD_IDS[LSCField.openingDate]:
      return FieldGroup.OPENING_HOURS;
    case LSCField.primaryPhone:
    case LSCField.additionalPhone:
    case ALTERNATIVE_FIELD_IDS[LSCField.additionalPhone]:
    case LSCField.phoneNumbers: //primary and additional phones
    case LSCField.primaryCategory:
    case LSCField.additionalCategory:
    case ALTERNATIVE_FIELD_IDS[LSCField.additionalCategory]:
    case LSCField.categories: //primary and additional categories
    case LSCField.fromTheBusiness:
    case ALTERNATIVE_FIELD_IDS[LSCField.fromTheBusiness]:
    case LSCField.labels:
    case LSCField.adwordsLocation:
    case ALTERNATIVE_FIELD_IDS[LSCField.adwordsLocation]:
    case LSCField.customServices:
    case LSCField.menuURL:
    case ALTERNATIVE_FIELD_IDS[LSCField.menuURL]:
      return FieldGroup.OTHER;
    case LSCField.appointmentURL:
    case ALTERNATIVE_FIELD_IDS[LSCField.appointmentURL]:
    case LSCField.orderAheadURL:
    case ALTERNATIVE_FIELD_IDS[LSCField.orderAheadURL]:
    case LSCField.reservationsURL:
    case ALTERNATIVE_FIELD_IDS[LSCField.reservationsURL]:
    case LSCField.socialMediaUrls:
      return FieldGroup.PLACE_ACTION_LINKS;
    case LSCField.lastImportErrorMessage:
    case LSCField.lastUpdateErrorMessage:
    case LSCField.acceptedDifferences:
    case LSCField.blackListedFields:
    case 'lastUpdated':
    case LSCField.editTimestamp:
    case 'attributes':
    case LSCField.verified: //todo rename BLS-2470
    case LSCField.managementStatus:
    case LSCField.dataGoldmine:
    case LSCField.claimAndCleanCompletionTimestamp:
    case LSCField.locationId:
    case LSCField.id:
      return FieldGroup.BLS_SPECIFIC;
    case LSCField.gmbAccountIds:
    case LSCField.existsInGMB:
    case LSCField.differentInGMB:
    case LSCField.myBusinessLocationUrl:
    case LSCField.searchUrl:
    case LSCField.mapsUrl:
    case LSCField.placeId:
    case LSCField.gmbLocationId:
      return FieldGroup.GMB_SPECIFIC;
    case LSCField.coverPhoto:
    case LSCField.logoPhoto:
    case LSCField.otherPhotos:
      return FieldGroup.MEDIA;
    default:
      return null;
  }
};

/**
 * Returns the fieldNames which can automatically be changed in GMB via BLS. For these fields functions like updating in
 * GMB, showing differences or getting blacklisted are possible. Ordered! Will show it this order in multiselection boxes.
 */
export const getChangeableGmbFields = (): string[] => {
  return [
    ALTERNATIVE_FIELD_IDS[LSCField.businessName],
    LSCField.address,
    ALTERNATIVE_FIELD_IDS[LSCField.website],
    ALTERNATIVE_FIELD_IDS[LSCField.businessStatus],
    LSCField.regularHours,

    LSCField.specialHours, //BE combines specialHours and gxSpecialHours before patch request
    ALTERNATIVE_FIELD_IDS[LSCField.openingDate],

    LSCField.coverPhoto,
    LSCField.logoPhoto,
    LSCField.otherPhotos, // note: otherPhotos can be blacklisted and updated on GBP but we don't support differences for this field right now

    LSCField.phoneNumbers, // primary & additional phones
    ALTERNATIVE_FIELD_IDS[LSCField.adwordsLocation],
    LSCField.categories, // primary & additional categories
    ALTERNATIVE_FIELD_IDS[LSCField.fromTheBusiness], // = profile description
    LSCField.labels,
    LSCField.customServices,
    ALTERNATIVE_FIELD_IDS[LSCField.menuURL], // note: menuURL can be blacklisted and updated on GBP but we don't support differences for this field right now

    ALTERNATIVE_FIELD_IDS[LSCField.appointmentURL],
    ALTERNATIVE_FIELD_IDS[LSCField.orderAheadURL],
    ALTERNATIVE_FIELD_IDS[LSCField.reservationsURL],
    LSCField.socialMediaUrls,
  ];
};

/**
 * Extends `getChangeableGmbFields` with `latLng` -- Validated Coordinates.
 * Though `latLng` isn't (yet) changeable in GMB, users may still want to filter for
 * locations with open `latLng` differences.
 */
export const getFilterableGmbFields = (): string[] => {
  return getChangeableGmbFields().concat(['latLng']);
};

/**
 * Returns a list of fieldIds that belong to a certain group.
 * @param group Returns a list of fieldIds that belong to this field group.
 * @param allFieldIds List of fieldIds you want to check for belonging to this group.
 */
export const getFieldIdsOfFieldGroup = (group: FieldGroup, allFieldIds: string[]): string[] => {
  let fieldIDsOfGroup: string[] = [];
  for (let fieldId of allFieldIds) {
    if (getFieldGroupOfFieldId(fieldId) === group) {
      fieldIDsOfGroup.push(fieldId);
    }
  }
  return fieldIDsOfGroup;
};

/**
 * Returns a list of fieldGroups that specific fieldIds belong in.
 * @param fieldIds List of field names for which the fieldGroups they belong to should be found
 */
export const getFieldGroupsOfFieldIds = (fieldIds: string[]): FieldGroup[] => {
  let fieldGroupsUnsorted: FieldGroup[] = [];
  for (let fieldId of fieldIds) {
    let group = getFieldGroupOfFieldId(fieldId);
    if (!fieldGroupsUnsorted.includes(group)) {
      fieldGroupsUnsorted.push(group);
    }
  }
  let fieldGroupsSorted: FieldGroup[] = [];
  for (let group of Object.entries(FieldGroup)) {
    if (fieldGroupsUnsorted.includes(FieldGroup[group[0]])) {
      fieldGroupsSorted.push(FieldGroup[group[0]]);
    }
  }
  return fieldGroupsSorted;
};

/**
 * Alternative field IDs sometimes show up in the differences - gets the primary fieldId which
 * serves as key in ALTERNATIVE_FIELD_IDS dictionary
 * @param fieldId
 * @private
 */
export const getPrimaryFieldId = (fieldId: string): string => {
  let primaryFieldId;
  Object.keys(ALTERNATIVE_FIELD_IDS).forEach(key => {
    if (fieldId === ALTERNATIVE_FIELD_IDS[key]) {
      primaryFieldId = key;
    }
  });
  return primaryFieldId || fieldId;
};


/**
 * Checks if a location has open/accepted (ignored) differences to its GBP location
 * @param location
 * @param mode
 */
export const hasDifferences = (location: Location, mode: 'open' | 'ignored') => {
  const hasDifferencesObject = (location: Location) => {
    return typeof location?.differences !== 'undefined'
        && location?.differences
        && Object.keys(location?.differences)?.length > 0;
  };

  if (hasDifferencesObject(location)) {
    for (let diff in location?.differences) {
      if (mode === 'open') {
        if (!location.differences[diff].ignored) {
          return true;
        }
      } else {
        if (location.differences[diff].ignored) {
          return true;
        }
      }
    }
  } else return false;
};



/**
 * A CC candidate will never have an account ID
 * true = CC Location, false = LSC Location
 * @param location
 */
export const isCcCandidate = (location: Location | CCLocation) => {
  return !location?.accountId;
};

/**
 * Determines 'blacklist' status for a given field
 * @param location
 * @param fieldId
 */
export const isLocationFieldBlacklisted = (fieldId: LSCField, location: Location): boolean => {
  if (!location?.blackListedFields) {  // so empty / failed rows don't throw errors
    return;
  }

  if (location?.blackListedFields?.includes('address') && ADDRESS_SUBFIELDS.includes(fieldId)) {
    return true;
    // https://gxperts.atlassian.net/wiki/spaces/PD/pages/33528/LSC+-+Location+Management+Address+list#Handling-of-gx-Special-Hours
  } else if (location?.blackListedFields?.includes('specialHours') && fieldId === 'gxSpecialHours') {
    return true;
  } else {
    return location?.blackListedFields?.includes(fieldId) ||
        location?.blackListedFields?.includes(ALTERNATIVE_FIELD_IDS[fieldId]);
  }

};

/**
 * FE check
 * @param location
 * @param ongoingActions
 */
export const canCreateGbpConnection = (location: Location, ongoingActions: OngoingActionsEnum[]): boolean => {
  return !ongoingActions.includes(OngoingActionsEnum.CREATE_GBP_CONNECTION) && location && !location.foundInMyBusiness;
};

/**
 * Get the usual LSCField
 * @param alternativeFieldID
 */
export const getOriginalFieldID = (alternativeFieldID: string): string => {
  const entry = Object.entries(ALTERNATIVE_FIELD_IDS)
      .find(entry => entry[1] === alternativeFieldID);
  return entry ? entry[0] : undefined;
};

/**
 * Checks if a location has blacklisted fields.
 * @param location
 */
export const hasBlacklistedFields = (location: Location) => {
  return location?.blackListedFields?.length > 0;
}
